import React from "react";
import { Spinner } from "@fluentui/react-components";
import { CardProps } from "#root/components/ActionCards";
import ActionCards from "#root/components/ActionCards";
import { getCurrentUser, getIsCurrentUserLoading } from "#root/redux/auth/selectors";
import { ROUTES } from "#root/routes/constants";
import { useTenantById } from "#root/queries/tenants";
import "./styles.scss";
import { ROLES } from "#root/utils/roles";
import { TENANT_TYPE } from "#root/utils/tenants";
import { useCurrentTenant } from "#root/queries/local";
import { useSelector } from "react-redux";
import OrganizationsList from "./OrganizationsList";
import { useNavigate } from "react-router-dom";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector(getCurrentUser);
  const loading = useSelector(getIsCurrentUserLoading);
  const userRole = currentUser?.role ?? ROLES.VIEWER;
  const isGlobalAdmin = userRole.toUpperCase() === ROLES.GLOBALADMIN;

  const { data: currentTenantId } = useCurrentTenant();
  const { data: tenantResponse } = useTenantById({ id: currentTenantId || '' });
  const currentTenant = tenantResponse?.item;
  const tenantType = currentTenant?.type || TENANT_TYPE.RAPID_REUNIFICATION;

  const generalCards: Record<string, CardProps> = {
    vadmin: { image: "landing-vadmin", title: "VAdmin", onClick: () => navigate(ROUTES.VADMIN.ROOT) },
    vtracker: { image: "landing-vtracker", title: "VTracker", onClick: () => navigate(ROUTES.VTRACKER.ROOT) },
    organizationSettings: { image: "landing-organization-settings", title: "Organization Settings", onClick: () => navigate(ROUTES.ORGANIZATION_SETTINGS.ROOT) },
    viewerMap: { image: "landing-map-victims", title: "Viewer Map", onClick: () => navigate(ROUTES.VIEWER_MAP) },
    emcMerrgeSettings: { image: "emc-merrge-settings", title: "EMC MERRGE Settings", onClick: () => navigate(ROUTES.EMC_MERRGE_SETTINGS.ROOT) },
    merrge211: { image: "merrge211", title: "MERRGE™ 211", onClick: () => navigate(ROUTES.MERRGE211.ROOT) }
  };

  const defaultRoleCards: Record<string, CardProps[]> = {
    [ROLES.ADMIN]: [generalCards.vadmin, generalCards.vtracker, generalCards.organizationSettings, generalCards.viewerMap],
    [ROLES.MANAGER]: [generalCards.vadmin, generalCards.vtracker, generalCards.viewerMap],
    [ROLES.USER]: [generalCards.vtracker],
    [ROLES.VIEWER]: [generalCards.viewerMap],
    [ROLES.GLOBALADMIN]: [generalCards.emcMerrgeSettings]
  };

  const tenantOverrides: Record<string, Partial<Record<string, CardProps[]>>> = {
    [TENANT_TYPE.MERRGE_211]: {
      [ROLES.ADMIN]: [generalCards.merrge211, generalCards.organizationSettings],
      [ROLES.MANAGER]: [generalCards.merrge211]
    }
  };

  const getGlobalAdminTenantCards = (tenantType: string): CardProps[] => {
    if (tenantType === TENANT_TYPE.MERRGE_211) {
      return [generalCards.merrge211, generalCards.organizationSettings];
    }
    return [generalCards.vadmin, generalCards.vtracker, generalCards.organizationSettings];
  };

  const getCards = () => {
    if (isGlobalAdmin && currentTenantId) {
      return getGlobalAdminTenantCards(tenantType).map((card: CardProps) => ({ ...card }));
    }
    
    const roleKey = userRole.toUpperCase();
    
    const defaultCards = defaultRoleCards[roleKey] || [];
    const tenantOverride = tenantOverrides[tenantType]?.[roleKey];
    const cardsToUse = tenantOverride || defaultCards;
    
    return cardsToUse.map((card: CardProps) => ({ ...card }));
  };

  if (loading || !currentUser || (currentTenantId && !currentTenant)) {
    return (
      <Spinner size="large" />
    );
  }

  return (
    <div className="home-page">
      <h2>
        {currentTenant?.organizationName || currentTenant?.id ? (
          <>
            {currentTenant.organizationName || currentTenant.id}
            <div className="divider" />
          </>
        ) : null}
      </h2>
      <h3>Apps You Have Access to <div className="divider" /></h3>
      <ActionCards cards={getCards()} />
      {isGlobalAdmin && !currentTenantId && <OrganizationsList />}
    </div>
  );
};

export default Home;